import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { AspectOverride, Item } from "../types.ts";
import { applyAspectOverride } from "../lib/player/aspectRatio.ts";
import { DisplayControl } from "./DisplayControl.tsx";

/**
 * Player — wraps the native HTML5 <video> element. The element is the
 * source of truth for playback position; React reads from it, never the
 * other way around (Constitution + research R6).
 *
 * Exposes an imperative handle so the parent's keyboard handler and
 * MediaSession wiring can drive the element without lifting playback
 * state into React.
 */

export interface PlayerHandle {
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seekRelative: (seconds: number) => void;
  toggleMute: () => void;
  toggleFullscreen: () => void;
  togglePiP: () => void;
}

export interface PlayerProps {
  item: Item;
  isDemo?: boolean;
  onEnded?: () => void;
  onShowShortcuts?: () => void;
  crtActive?: boolean;
  crtDisabled?: boolean;
  crtDisabledTooltip?: string;
  crtLoading?: boolean;
  onCrtToggle?: () => void;
  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
  crtGlowStyle?: React.CSSProperties;
}

const OVERRIDES: { value: AspectOverride; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "4:3", label: "4:3" },
  { value: "16:9", label: "16:9" },
  { value: "1.37", label: "1.37 Academy" },
  { value: "1.85", label: "1.85" },
  { value: "2.35", label: "2.35" },
  { value: "native", label: "Native pixels" },
];

function fmtTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export const Player = forwardRef<PlayerHandle, PlayerProps>(function Player(
  { item, isDemo, onEnded, onShowShortcuts, crtActive, crtDisabled, crtDisabledTooltip, crtLoading, onCrtToggle, canvasRef, crtGlowStyle },
  ref,
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [override, setOverride] = useState<AspectOverride>("auto");
  const [needsTapToStart, setNeedsTapToStart] = useState(false);
  const [paused, setPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(!!isDemo);

  // Track playback state from the video element.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPaused(false);
    const onPause = () => setPaused(true);
    const onTime = () => setCurrentTime(v.currentTime);
    const onMeta = () => setDuration(v.duration || 0);
    const onVol = () => setMuted(v.muted);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("volumechange", onVol);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("volumechange", onVol);
    };
  }, [item.streamUrl]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => undefined);
    else v.pause();
  }, []);

  const seek = useCallback((t: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
  }, []);

  // Reset override on item change so each item starts with its own ratio.
  useEffect(() => {
    setOverride("auto");
  }, [item.identifier]);

  // Attempt to play whenever the source changes. If autoplay is blocked,
  // surface the tap-to-start affordance once.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || item.isEmbedFallback) return;
    setNeedsTapToStart(false);
    const attempt = v.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => setNeedsTapToStart(true));
    }
  }, [item.streamUrl, item.isEmbedFallback]);

  useImperativeHandle(
    ref,
    () => ({
      play() {
        void videoRef.current?.play().catch(() => undefined);
      },
      pause() {
        videoRef.current?.pause();
      },
      togglePlay() {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) void v.play().catch(() => undefined);
        else v.pause();
      },
      seekRelative(seconds: number) {
        const v = videoRef.current;
        if (!v) return;
        v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + seconds));
      },
      toggleMute() {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
      },
      toggleFullscreen() {
        const stage = stageRef.current;
        if (!stage) return;
        if (document.fullscreenElement) void document.exitFullscreen();
        else void stage.requestFullscreen();
      },
      async togglePiP() {
        const v = videoRef.current;
        if (!v) return;
        try {
          if (document.pictureInPictureElement) {
            await document.exitPictureInPicture();
          } else {
            await v.requestPictureInPicture();
          }
        } catch {
          /* not supported — swallow */
        }
      },
    }),
    [],
  );

  const resolvedAr = applyAspectOverride(override, item);
  const stageStyle = {
    ["--item-aspect" as string]: `${resolvedAr}`,
  } as React.CSSProperties;

  if (item.isEmbedFallback) {
    return (
      <div className="player">
        <div className="player-stage" ref={stageRef} style={stageStyle}>
          <div className="player-loading">No browser-playable file available</div>
        </div>
        <p className="notice warning">
          This item has no MP4 derivative we can auto-advance.{" "}
          <a href={item.detailsUrl} target="_blank" rel="noreferrer">
            Open it on archive.org
          </a>{" "}
          to watch.
        </p>
      </div>
    );
  }

  return (
    <div className="player">
      <div
        className={`player-stage${crtActive ? " crt-active" : ""}`}
        ref={stageRef}
        style={{ ...stageStyle, ...(crtActive && crtGlowStyle ? crtGlowStyle : {}) }}
      >
        <video
          ref={videoRef}
          src={item.streamUrl}
          poster={item.thumbUrl}
          crossOrigin="anonymous"
          playsInline
          preload="metadata"
          muted={isDemo}
          onEnded={onEnded}
          style={crtActive ? { visibility: "hidden" as const } : undefined}
        />
        <canvas
          ref={canvasRef as React.RefObject<HTMLCanvasElement>}
          className="crt-surface"
          onClick={() => {
            const v = videoRef.current;
            if (!v) return;
            if (v.paused) void v.play().catch(() => undefined);
            else v.pause();
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: crtActive ? "block" : "none",
            cursor: "pointer",
          }}
        />
        {isDemo && !crtActive && <div className="player-demo-badge">Demo channel</div>}
        {needsTapToStart && (
          <button
            className="player-tap-to-start"
            onClick={() => {
              setNeedsTapToStart(false);
              void videoRef.current?.play();
            }}
          >
            Tap to start
          </button>
        )}
      </div>
      {/* Scrub bar — always visible, spans full width */}
      <div className="player-scrub">
        <input
          type="range"
          min={0}
          max={duration || 1}
          step={0.1}
          value={currentTime}
          onChange={(e) => seek(parseFloat(e.target.value))}
          aria-label="Seek"
          style={{ width: "100%" }}
        />
      </div>
      <div className="player-chrome">
        <button onClick={togglePlay} aria-label={paused ? "Play" : "Pause"} title={paused ? "Play (Space)" : "Pause (Space)"}>
          {paused ? "\u25B6" : "\u23F8"}
        </button>
        <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} title="Mute (M)">
          {muted ? "\uD83D\uDD07" : "\uD83D\uDD0A"}
        </button>
        <span className="paste-box-hint" style={{ minWidth: "5rem", textAlign: "center" }}>
          {fmtTime(currentTime)} / {fmtTime(duration)}
        </span>
        <label>
          <span className="paste-box-hint" style={{ marginRight: "0.5rem" }}>
            Aspect
          </span>
          <select
            value={override}
            onChange={(e) => setOverride(e.target.value as AspectOverride)}
            aria-label="Aspect ratio override"
          >
            {OVERRIDES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
        <button
          onClick={() => {
            const stage = stageRef.current;
            if (!stage) return;
            if (document.fullscreenElement) void document.exitFullscreen();
            else void stage.requestFullscreen();
          }}
          aria-label="Fullscreen"
          title="Fullscreen (F)"
        >
          Fullscreen
        </button>
        <button
          onClick={async () => {
            const v = videoRef.current;
            if (!v) return;
            try {
              if (document.pictureInPictureElement) await document.exitPictureInPicture();
              else await v.requestPictureInPicture();
            } catch {
              /* swallow */
            }
          }}
          aria-label="Picture in picture"
          title="Picture-in-picture (P)"
        >
          PiP
        </button>
        {onCrtToggle && (
          <DisplayControl
            active={!!crtActive}
            disabled={crtDisabled}
            disabledTooltip={crtDisabledTooltip}
            onToggle={onCrtToggle}
            loading={crtLoading}
          />
        )}
        {onShowShortcuts && (
          <button onClick={onShowShortcuts} aria-label="Keyboard shortcuts" title="Shortcuts (?)">
            ?
          </button>
        )}
      </div>
    </div>
  );
});
